import { create } from 'zustand';
import { SETTINGS_TABS, isSettingsTab, type SettingsTabId } from './registry.ts';

interface SettingsState {
  open: boolean;
  /** The chosen tab; null means none yet: the first tab on a desktop, the tab list on a phone. */
  tab: SettingsTabId | null;
  /** Where focus goes back to when the panel closes. */
  opener: HTMLElement | null;
  openSettings: (tab?: string) => void;
  close: () => void;
  selectTab: (tab: SettingsTabId) => void;
  /** Phone: from a tab's detail screen back to the tab list. */
  back: () => void;
}

export const useSettings = create<SettingsState>((set) => ({
  open: false,
  tab: null,
  opener: null,
  openSettings: (tab) =>
    set({
      open: true,
      // An unknown id is ignored rather than opening an empty panel.
      tab: tab && isSettingsTab(tab) ? tab : null,
      opener: typeof document !== 'undefined' && document.activeElement instanceof HTMLElement ? document.activeElement : null,
    }),
  close: () => set({ open: false, tab: null }),
  selectTab: (tab) => set({ tab }),
  back: () => set({ tab: null }),
}));

/** Opens Ayarlar from anywhere, optionally straight on a tab: `openSettings('konumlar')`. */
export const openSettings = (tab?: string) => useSettings.getState().openSettings(tab);

/** The tab to show on a desktop, where one is always selected. */
export const resolveTab = (tab: SettingsTabId | null): SettingsTabId => tab ?? SETTINGS_TABS[0].id;

/** The tab after `tab` in the given direction, wrapping around; used for arrow-key navigation. */
export function neighbourTab(tab: SettingsTabId, delta: 1 | -1): SettingsTabId {
  const index = SETTINGS_TABS.findIndex((item) => item.id === tab);
  return SETTINGS_TABS[(index + delta + SETTINGS_TABS.length) % SETTINGS_TABS.length]!.id;
}
